import { useEffect, useState } from "react";
import {
  AdminDiscountCode,
  createDiscountCode,
  deleteDiscountCode,
  DiscountCodeType,
  DiscountCodeUsageType,
  updateDiscountCodeDates,
  listDiscountCodes,
} from "../../api/discountCodes";
import { listEventTicketSets } from "../../api/ticketSets";
import ErrorAlert from "../ErrorAlert/ErrorAlert";
import LoadingPanel from "../LoadingPanel/LoadingPanel";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";
import { dateInputToIso, toDateInputValue } from "../../utils/eventDates";

type Props = { eventId: number };

type TicketTypeOption = { id: number; name: string };

const STATUS_STYLES: Record<AdminDiscountCode["status"], string> = {
  active: "bg-green-50 text-green-800 border border-green-200",
  scheduled: "bg-amber-50 text-amber-900 border border-amber-200",
  expired: "bg-slate-100 text-slate-600 border border-slate-200",
  used: "bg-slate-100 text-slate-600 border border-slate-200",
};

const STATUS_LABELS: Record<AdminDiscountCode["status"], string> = {
  active: "Active",
  scheduled: "Scheduled",
  expired: "Expired",
  used: "Used",
};

function formatDiscount(type: DiscountCodeType, value: number): string {
  return type === "percent" ? `${value}% off` : `$${(value / 100).toFixed(2)} off`;
}

function formatDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "No end";
}

export default function DiscountCodesPanel({ eventId }: Props) {
  const [codes, setCodes] = useState<AdminDiscountCode[]>([]);
  const [ticketTypes, setTicketTypes] = useState<TicketTypeOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [codesResult, sets] = await Promise.all([
        listDiscountCodes(eventId),
        listEventTicketSets(eventId),
      ]);
      setCodes(codesResult);
      setTicketTypes(
        sets.flatMap((set) =>
          set.ticketTypes
            .filter((t) => t.status === "active")
            .map((t) => ({ id: t.id, name: t.name })),
        ),
      );
    } catch (err) {
      setError(getUserFacingErrorMessage(err, "Unable to load discount codes."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [eventId]);

  const ticketTypeName = (id: number) =>
    ticketTypes.find((t) => t.id === id)?.name ?? `#${id}`;

  async function handleDelete(id: number) {
    if (!window.confirm("Delete this discount code?")) return;
    try {
      await deleteDiscountCode(eventId, id);
      await load();
    } catch (err) {
      setError(getUserFacingErrorMessage(err, "Could not delete discount code."));
    }
  }

  if (isLoading) {
    return <LoadingPanel title="Loading discount codes" className="py-8" />;
  }

  return (
    <div className="space-y-6">
      {error && <ErrorAlert>{error}</ErrorAlert>}

      <CreateDiscountCodeForm
        eventId={eventId}
        ticketTypes={ticketTypes}
        onCreated={load}
      />

      {codes.length === 0 ? (
        <p className="text-slate-600 text-sm">No discount codes yet.</p>
      ) : (
        <div className="border rounded-sm overflow-x-auto bg-white">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="p-2">Code</th>
                <th className="p-2">Usage</th>
                <th className="p-2">Discount</th>
                <th className="p-2">Applies to</th>
                <th className="p-2">Start</th>
                <th className="p-2">End</th>
                <th className="p-2">Status</th>
                <th className="p-2">Redeemed</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code.id} className="border-t align-top">
                  <td className="p-2 font-mono">{code.code}</td>
                  <td className="p-2 capitalize">{code.usageType.replace("_", " ")}</td>
                  <td className="p-2">{formatDiscount(code.discountType, code.discountValue)}</td>
                  <td className="p-2 max-w-[16rem]">
                    {code.ticketTypeIds.map(ticketTypeName).join(", ")}
                  </td>
                  {editingId === code.id ? (
                    <EditDatesRow
                      eventId={eventId}
                      code={code}
                      onDone={() => {
                        setEditingId(null);
                        load();
                      }}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <>
                      <td className="p-2">{formatDate(code.startsAt)}</td>
                      <td className="p-2">{formatDate(code.endsAt)}</td>
                      <td className="p-2">
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-sm ${STATUS_STYLES[code.status]}`}
                        >
                          {STATUS_LABELS[code.status]}
                        </span>
                      </td>
                      <td className="p-2">{code.redeemedCount}</td>
                      <td className="p-2 whitespace-nowrap">
                        <button
                          type="button"
                          className="text-slate-600 underline mr-3"
                          onClick={() => setEditingId(code.id)}
                        >
                          Edit dates
                        </button>
                        {code.redeemedCount === 0 && (
                          <button
                            type="button"
                            className="text-red-700 underline"
                            onClick={() => handleDelete(code.id)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EditDatesRow({
  eventId,
  code,
  onDone,
  onCancel,
}: {
  eventId: number;
  code: AdminDiscountCode;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [startsAt, setStartsAt] = useState(
    code.startsAt ? toDateInputValue(code.startsAt) : "",
  );
  const [endsAt, setEndsAt] = useState(
    code.endsAt ? toDateInputValue(code.endsAt) : "",
  );
  const [noEnd, setNoEnd] = useState(!code.endsAt);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setIsSaving(true);
    setError(null);
    try {
      await updateDiscountCodeDates(eventId, code.id, {
        startsAt: startsAt ? dateInputToIso(startsAt) : null,
        endsAt: noEnd || !endsAt ? null : dateInputToIso(endsAt, true),
      });
      onDone();
    } catch (err) {
      setError(getUserFacingErrorMessage(err, "Could not update dates."));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <td colSpan={5} className="p-2">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-slate-600">
          Start date
          <input
            type="date"
            className="border p-1 text-sm"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </label>
        <label className="flex flex-col text-xs text-slate-600">
          End date
          <input
            type="date"
            className="border p-1 text-sm disabled:bg-slate-100"
            value={endsAt}
            disabled={noEnd}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-1 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={noEnd}
            onChange={(e) => setNoEnd(e.target.checked)}
          />
          No end
        </label>
        <button
          type="button"
          className="eh-btn-primary text-xs px-3 py-1.5"
          disabled={isSaving}
          onClick={handleSave}
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="eh-btn-secondary text-xs px-3 py-1.5"
          disabled={isSaving}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
      {error && <ErrorAlert className="mt-2" inline>{error}</ErrorAlert>}
    </td>
  );
}

function CreateDiscountCodeForm({
  eventId,
  ticketTypes,
  onCreated,
}: {
  eventId: number;
  ticketTypes: TicketTypeOption[];
  onCreated: () => void;
}) {
  const [code, setCode] = useState("");
  const [usageType, setUsageType] = useState<DiscountCodeUsageType>("single_use");
  const [discountType, setDiscountType] = useState<DiscountCodeType>("percent");
  const [discountValue, setDiscountValue] = useState("");
  const [selectedTicketTypeIds, setSelectedTicketTypeIds] = useState<number[]>([]);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [noEnd, setNoEnd] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleTicketType(id: number) {
    setSelectedTicketTypeIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const value = Number(discountValue);
    if (!code.trim()) {
      setError("Enter a code.");
      return;
    }
    if (selectedTicketTypeIds.length === 0) {
      setError("Select at least one ticket type this code applies to.");
      return;
    }
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter a discount value greater than 0.");
      return;
    }

    setIsSaving(true);
    try {
      await createDiscountCode(eventId, {
        code: code.trim(),
        usageType,
        discountType,
        discountValue:
          discountType === "percent" ? Math.round(value) : Math.round(value * 100),
        ticketTypeIds: selectedTicketTypeIds,
        startsAt: startsAt ? dateInputToIso(startsAt) : null,
        endsAt: noEnd || !endsAt ? null : dateInputToIso(endsAt, true),
      });
      setCode("");
      setDiscountValue("");
      setSelectedTicketTypeIds([]);
      setStartsAt("");
      setEndsAt("");
      setNoEnd(true);
      onCreated();
    } catch (err) {
      setError(getUserFacingErrorMessage(err, "Could not create discount code."));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border rounded-sm bg-white p-4 space-y-4 max-w-2xl"
    >
      <p className="font-semibold text-sm">New discount code</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="flex flex-col text-xs text-slate-600 gap-1">
          Code
          <input
            type="text"
            className="border p-2 text-sm uppercase"
            placeholder="e.g. EARLYBIRD20"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>

        <label className="flex flex-col text-xs text-slate-600 gap-1">
          Usage
          <select
            className="border p-2 text-sm bg-white"
            value={usageType}
            onChange={(e) => setUsageType(e.target.value as DiscountCodeUsageType)}
          >
            <option value="single_use">Single use — expires after one paid order</option>
            <option value="multi_use">Multi use — unlimited, until it expires</option>
          </select>
        </label>

        <label className="flex flex-col text-xs text-slate-600 gap-1">
          Discount type
          <select
            className="border p-2 text-sm bg-white"
            value={discountType}
            onChange={(e) => setDiscountType(e.target.value as DiscountCodeType)}
          >
            <option value="percent">Percent off</option>
            <option value="fixed">Fixed amount off</option>
          </select>
        </label>

        <label className="flex flex-col text-xs text-slate-600 gap-1">
          {discountType === "percent" ? "Percent off (1-100)" : "Amount off ($)"}
          <input
            type="number"
            min={discountType === "percent" ? 1 : 0.01}
            max={discountType === "percent" ? 100 : undefined}
            step={discountType === "percent" ? 1 : 0.01}
            className="border p-2 text-sm"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
          />
        </label>
      </div>

      <div>
        <p className="text-xs text-slate-600 mb-1">Applies to ticket types</p>
        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border p-2 rounded-sm">
          {ticketTypes.length === 0 ? (
            <p className="text-xs text-slate-500">
              No active ticket types found for this event yet.
            </p>
          ) : (
            ticketTypes.map((t) => (
              <label
                key={t.id}
                className="flex items-center gap-1 text-xs bg-slate-50 border border-slate-200 px-2 py-1 rounded-sm"
              >
                <input
                  type="checkbox"
                  checked={selectedTicketTypeIds.includes(t.id)}
                  onChange={() => toggleTicketType(t.id)}
                />
                {t.name}
              </label>
            ))
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-slate-600">
          Start date (optional)
          <input
            type="date"
            className="border p-2 text-sm"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </label>
        <label className="flex flex-col text-xs text-slate-600">
          End date
          <input
            type="date"
            className="border p-2 text-sm disabled:bg-slate-100"
            value={endsAt}
            disabled={noEnd}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-1 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={noEnd}
            onChange={(e) => setNoEnd(e.target.checked)}
          />
          No end
        </label>
      </div>

      {error && <ErrorAlert inline>{error}</ErrorAlert>}

      <button type="submit" className="eh-btn-primary text-sm" disabled={isSaving}>
        {isSaving ? "Creating…" : "Create discount code"}
      </button>
    </form>
  );
}
